import React, { useState, useEffect } from 'react';
import { Calendar, Users, Clock, Sparkles, CheckCircle2, MapPin } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VesakRegistration = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [totalPeople, setTotalPeople] = useState(0);

  // Fetch registration count on page load
  useEffect(() => {
    fetchRegistrationCount();
  }, []);

  const fetchRegistrationCount = async () => {
    try {
      const response = await axios.get(`${API}/vesak-registration/count`);
      setTotalPeople(response.data.totalPeople);
    } catch (error) {
      console.error('Error fetching registration count:', error);
    }
  };
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    numberOfPeople: '1',
    morningProgram: false,
    eveningProgram: false,
    specialRequirements: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.morningProgram && !formData.eveningProgram) {
      toast({
        title: "Please select a program",
        description: "Choose at least one program (Morning or Evening)",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);

    try {
      // Submit to backend
      await axios.post(`${API}/vesak-registration`, formData);

      const programs = [];
      if (formData.morningProgram) programs.push('Morning Program');
      if (formData.eveningProgram) programs.push('Evening Program');

      toast({
        title: "Registration Successful! 🙏",
        description: `Thank you ${formData.name}! You are registered for: ${programs.join(' & ')}`,
      });

      // Refresh the registration count
      await fetchRegistrationCount();

      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        numberOfPeople: '1',
        morningProgram: false,
        eveningProgram: false,
        specialRequirements: ''
      });
    } catch (error) {
      console.error('Registration error:', error);
      toast({
        title: "Registration Failed",
        description: "There was an error submitting your registration. Please try again or call us at (727) 504-0518",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCheckboxChange = (field) => {
    setFormData(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section 
        className="relative h-[400px] flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/6689733/pexels-photo-6689733.jpeg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 to-black/60"></div>
        <div className="relative z-10 text-center px-4">
          <Sparkles className="mx-auto text-amber-700 mb-4 animate-pulse" size={56} />
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">Register for Vesak 2026</h1>
          <p className="text-2xl text-amber-100 mb-2">Saturday, May 2, 2026</p>
          <p className="text-xl text-white/90">Alabama Buddhist Vihara & Meditation Center</p>
        </div>
      </section>

      {/* Registration Form */}
      <section className="py-20 bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <Users className="mx-auto text-amber-700 mb-4" size={40} />
            <h2 className="text-4xl font-bold text-amber-900 mb-4">Register for Vesak 2026</h2>
            <p className="text-lg text-gray-700">Sign up to join us for this sacred celebration</p>
            
            {/* Registration Count Display */}
            {totalPeople > 0 && (
              <div className="mt-6 inline-block bg-gradient-to-r from-green-500 to-emerald-500 text-white px-8 py-4 rounded-full shadow-lg animate-pulse">
                <p className="text-2xl font-bold">
                  🎉 {totalPeople} {totalPeople === 1 ? 'person' : 'people'} registered so far!
                </p>
              </div>
            )}
          </div>

          <Card className="border-4 border-amber-300 shadow-2xl">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name */}
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-amber-900 mb-2">
                    Full Name *
                  </label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="border-2 border-amber-200 focus:border-amber-400"
                    placeholder="Enter your full name"
                  />
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-amber-900 mb-2">
                    Email Address *
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="border-2 border-amber-200 focus:border-amber-400"
                    placeholder="your.email@example.com"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-amber-900 mb-2">
                    Phone Number *
                  </label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    className="border-2 border-amber-200 focus:border-amber-400"
                    placeholder="(123) 456-7890"
                  />
                </div>

                {/* Number of People */}
                <div>
                  <label htmlFor="numberOfPeople" className="block text-sm font-semibold text-amber-900 mb-2">
                    Number of People Attending *
                  </label>
                  <Input
                    id="numberOfPeople"
                    name="numberOfPeople"
                    type="number"
                    min="1"
                    max="20"
                    required
                    value={formData.numberOfPeople}
                    onChange={handleChange}
                    className="border-2 border-amber-200 focus:border-amber-400"
                  />
                </div>

                {/* Program Selection */}
                <div className="space-y-4">
                  <label className="block text-sm font-semibold text-amber-900 mb-3">
                    Select Program(s) to Attend *
                  </label>
                  
                  <div className="flex items-start space-x-3 bg-white p-4 rounded-lg border-2 border-amber-200">
                    <Checkbox
                      id="morningProgram"
                      checked={formData.morningProgram}
                      onCheckedChange={() => handleCheckboxChange('morningProgram')}
                      className="mt-1"
                    />
                    <label
                      htmlFor="morningProgram"
                      className="flex-1 cursor-pointer"
                    >
                      <div className="font-semibold text-amber-900">Meditation Retreat</div>
                      <div className="text-sm text-gray-600">7:00 AM - 4:00 PM</div>
                    </label>
                  </div>

                  <div className="flex items-start space-x-3 bg-white p-4 rounded-lg border-2 border-amber-200">
                    <Checkbox
                      id="eveningProgram"
                      checked={formData.eveningProgram}
                      onCheckedChange={() => handleCheckboxChange('eveningProgram')}
                      className="mt-1"
                    />
                    <label
                      htmlFor="eveningProgram"
                      className="flex-1 cursor-pointer"
                    >
                      <div className="font-semibold text-amber-900">Vesak Celebration & Opening</div>
                      <div className="text-sm text-gray-600">4:00 PM - 7:00 PM</div>
                    </label>
                  </div>
                </div>

                {/* Special Requirements */}
                <div>
                  <label htmlFor="specialRequirements" className="block text-sm font-semibold text-amber-900 mb-2">
                    Special Requirements (Optional)
                  </label>
                  <Input
                    id="specialRequirements"
                    name="specialRequirements"
                    type="text"
                    value={formData.specialRequirements}
                    onChange={handleChange}
                    className="border-2 border-amber-200 focus:border-amber-400"
                    placeholder="Dietary restrictions, accessibility needs, etc."
                  />
                </div>

                {/* Submit Button */}
                <Button 
                  type="submit" 
                  disabled={loading}
                  className="w-full bg-amber-600 hover:bg-amber-700 text-white py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all"
                >
                  {loading ? 'Submitting...' : 'Register for Vesak Festival'}
                  <Calendar className="ml-2" size={20} />
                </Button>

                <p className="text-sm text-gray-600 text-center mt-4">
                  You will receive a confirmation email after registration
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Important Information */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <Card className="border-2 border-amber-200 bg-amber-50">
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold text-amber-900 mb-4">All Are Welcome</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-amber-600 font-bold">🙏</span>
                  <span><strong>Everyone is warmly welcome</strong> — Buddhists and non-Buddhists, families, children, friends, and neighbors</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-600 font-bold">📍</span>
                  <span><strong>Location:</strong> 2100 Hardwick Road, Pell City, Alabama 35128</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-600 font-bold">🎊</span>
                  <span><strong>Grand Opening</strong> — Official opening and offering of our new Vihara as a peaceful spiritual home for the community</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-600 font-bold">🧘</span>
                  <span><strong>Meditation Retreat</strong> is open to everyone, including beginners — No experience required</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-600 font-bold">🍛</span>
                  <span><strong>Meals provided</strong> — Lunch and tea during retreat, free food offering in the evening</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-600 font-bold">👨‍👩‍👧‍👦</span>
                  <span><strong>Children welcome</strong> — Special children's performances and activities planned</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-600 font-bold">🏮</span>
                  <span>Experience traditional Vesak lanterns, candle lighting, devotional songs, and community blessings</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-600 font-bold">📧</span>
                  <span><strong>Contact:</strong> alvihara@gmail.com or call (727) 504-0518 / (205) 201-2881</span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default VesakRegistration;
